import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, KeyboardArrowDown, KeyboardArrowRight, PersonAdd } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useToast } from '../contexts/ToastContext';
import {
  DataTableShell,
  EmptyState,
  FormDialog,
  PageContainer,
  PageHeader,
  StatusChip,
  TableSkeleton,
} from '../components/ui';

type GroupKind = 'buying_group' | 'multi_store';
type MemberStatus = 'invited' | 'active' | 'left';

interface GroupMember {
  organizationId: string;
  status: MemberStatus;
  invitedAt: string | null;
  acceptedAt: string | null;
  leftAt: string | null;
}

interface AdminGroup {
  id: string;
  name: string;
  kind: GroupKind;
  parentOrganizationId: string;
  status: 'active' | 'archived';
  memberCount: number;
  members: GroupMember[];
}

const GROUPS_QUERY_KEY = ['admin-organization-groups'];
const STATUS_TONE: Record<MemberStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  invited: 'warning',
  left: 'neutral',
};

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await makeApiCall(path, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || 'Request failed');
  }
  return body.data as T;
}

/**
 * Alex's view of managed groups: create a buying group or head office, see
 * its member stores, and attach one whose consent was taken off-platform.
 * A no-audience platform tool would be a System tab; this has its own
 * audience (dealer group onboarding) and a drill-in, so it gets its own
 * route and nav item per the tab-vs-route rule.
 */
export const AdminOrganizationGroupsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState<AdminGroup | null>(null);

  const [name, setName] = useState('');
  const [kind, setKind] = useState<GroupKind>('buying_group');
  const [parentOrganizationId, setParentOrganizationId] = useState('');
  const [attachOrganizationId, setAttachOrganizationId] = useState('');
  const [parentTouched, setParentTouched] = useState(false);
  const [attachTouched, setAttachTouched] = useState(false);
  const orgIdProblem = (value: string): boolean => !value.trim() || /[\s/]/.test(value.trim());

  const { data, isLoading, isError } = useQuery({
    queryKey: GROUPS_QUERY_KEY,
    queryFn: () => apiJson<AdminGroup[]>('/admin/groups'),
    staleTime: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson('/admin/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), kind, parentOrganizationId: parentOrganizationId.trim() }),
      }),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      setName('');
      setKind('buying_group');
      setParentOrganizationId('');
      showToast(t('admin.organizationGroups.toast.created'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const attachMutation = useMutation({
    mutationFn: () => {
      if (!attachTarget) throw new Error('No group selected');
      return apiJson(`/admin/groups/${attachTarget.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: attachOrganizationId.trim() }),
      });
    },
    onSuccess: () => {
      invalidate();
      setAttachTarget(null);
      setAttachOrganizationId('');
      showToast(t('admin.organizationGroups.toast.attached'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const groups = data ?? [];

  const newGroupButton = (
    <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
      {t('admin.organizationGroups.newGroup')}
    </Button>
  );
  const Wrapper = embedded ? React.Fragment : PageContainer;

  return (
    <Wrapper>
      {embedded ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{newGroupButton}</Box>
      ) : (
        <PageHeader
          title={t('admin.organizationGroups.title')}
          description={t('admin.organizationGroups.description')}
          actions={newGroupButton}
        />
      )}

      <DataTableShell>
        {isLoading && <Box sx={{ p: 2 }}><TableSkeleton rows={4} columns={4} /></Box>}
        {!isLoading && isError && (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title={t('admin.organizationGroups.errorTitle')}
              description={t('admin.organizationGroups.errorDescription')}
            />
          </Box>
        )}
        {!isLoading && !isError && groups.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title={t('admin.organizationGroups.emptyTitle')}
              description={t('admin.organizationGroups.emptyDescription')}
            />
          </Box>
        )}
        {!isLoading && !isError && groups.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('admin.organizationGroups.columns.name')}</TableCell>
                  <TableCell>{t('admin.organizationGroups.columns.kind')}</TableCell>
                  <TableCell>{t('admin.organizationGroups.columns.parent')}</TableCell>
                  <TableCell align="right">{t('admin.organizationGroups.columns.members')}</TableCell>
                  <TableCell align="right">{t('admin.organizationGroups.columns.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <React.Fragment key={group.id}>
                    <TableRow hover>
                      <TableCell sx={{ width: 40 }}>
                        <IconButton
                          size="small"
                          onClick={() => setExpanded(expanded === group.id ? null : group.id)}
                          aria-label={t('admin.organizationGroups.expand')}
                        >
                          {expanded === group.id ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{group.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {t(`admin.organizationGroups.kind.${group.kind}`)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{group.parentOrganizationId}</Typography>
                      </TableCell>
                      <TableCell align="right">{group.memberCount}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAdd />}
                          onClick={() => setAttachTarget(group)}
                        >
                          {t('admin.organizationGroups.attach')}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: expanded === group.id ? undefined : 'none' }}>
                        <Collapse in={expanded === group.id} unmountOnExit>
                          <Box sx={{ px: 3, py: 2, backgroundColor: 'action.hover' }}>
                            {group.members.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t('admin.organizationGroups.noMembers')}
                              </Typography>
                            ) : (
                              <Stack spacing={1}>
                                {group.members.map((member) => (
                                  <Stack
                                    key={member.organizationId}
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <Typography variant="body2" sx={{ minWidth: 220 }}>
                                      {member.organizationId}
                                    </Typography>
                                    <StatusChip
                                      label={t(`admin.organizationGroups.memberStatus.${member.status}`)}
                                      tone={STATUS_TONE[member.status]}
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTableShell>

      <FormDialog
        open={createOpen}
        title={t('admin.organizationGroups.newGroup')}
        onClose={() => setCreateOpen(false)}
        onSubmit={() => createMutation.mutate()}
        submitLabel={t('admin.organizationGroups.create')}
        submitDisabled={!name.trim() || !parentOrganizationId.trim() || createMutation.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('admin.organizationGroups.groupName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            select
            label={t('admin.organizationGroups.groupKind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as GroupKind)}
            fullWidth
          >
            <MenuItem value="buying_group">{t('admin.organizationGroups.kind.buying_group')}</MenuItem>
            <MenuItem value="multi_store">{t('admin.organizationGroups.kind.multi_store')}</MenuItem>
          </TextField>
          <TextField
            label={t('admin.organizationGroups.parentOrganizationId')}
            helperText={
              parentTouched && orgIdProblem(parentOrganizationId)
                ? t('admin.organizationGroups.orgIdInvalid')
                : t('admin.organizationGroups.parentOrgHelper')
            }
            error={parentTouched && orgIdProblem(parentOrganizationId)}
            value={parentOrganizationId}
            onChange={(e) => setParentOrganizationId(e.target.value)}
            onBlur={() => setParentTouched(true)}
            fullWidth
          />
        </Stack>
      </FormDialog>

      <FormDialog
        open={Boolean(attachTarget)}
        title={t('admin.organizationGroups.attachTitle', { group: attachTarget?.name || '' })}
        onClose={() => {
          setAttachTarget(null);
          setAttachOrganizationId('');
        }}
        onSubmit={() => attachMutation.mutate()}
        submitLabel={t('admin.organizationGroups.attach')}
        submitDisabled={!attachOrganizationId.trim() || attachMutation.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.organizationGroups.attachBody')}
          </Typography>
          <TextField
            label={t('admin.organizationGroups.attachOrgId')}
            value={attachOrganizationId}
            onChange={(e) => setAttachOrganizationId(e.target.value)}
            onBlur={() => setAttachTouched(true)}
            error={attachTouched && orgIdProblem(attachOrganizationId)}
            helperText={attachTouched && orgIdProblem(attachOrganizationId) ? t('admin.organizationGroups.orgIdInvalid') : undefined}
            fullWidth
            autoFocus
          />
        </Stack>
      </FormDialog>
    </Wrapper>
  );
};

export default AdminOrganizationGroupsPage;
